import { useState, useEffect } from "react";
import { API, apiCall } from "../api";
import { useAuth } from "../context/AuthContext";
import { calculateTimeOnPlatform } from "../utils/time";
import { VerificationGate, AdminVerifications } from "./Verification";
import { TenantSearch } from "./TenantSearch";
import { RateTenant } from "./RateTenant";
import { AnalyticsDashboard } from "./AnalyticsDashboard";
import { FounderBanner } from "./FounderBanner";
import { Logo } from "./Logo";
import { BrandBackdrop } from "./AuthLayout";
import { NotificationsBell } from "./NotificationsBell";

const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

// Small trust label under the landlord's name
const VerifiedLabel = ({ status }) => {
  if (status === 'verified') {
    return <p className="text-xs font-semibold text-emerald-700 flex items-center justify-end gap-1">
      <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z" clipRule="evenodd" /></svg>
      Propietario verificado
    </p>;
  }
  if (status === 'pending') return <p className="text-xs text-amber-700/80">Verificaci&oacute;n en revisi&oacute;n</p>;
  return <p className="text-xs text-amber-700/80">Identidad sin verificar</p>;
};

// Dashboard Component
const Dashboard = () => {
  const { user, logout, token } = useAuth();
  const [activeTab, setActiveTab] = useState('search');
  const [stats, setStats] = useState({});
  const [showProfile, setShowProfile] = useState(false);


  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      const result = await apiCall('/dashboard/stats', 'GET', null, token);
      setStats(result);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-amber-100 sticky top-0" style={{zIndex: 300}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-3 py-3 sm:py-0 sm:h-16">
            <Logo subtitle="Portal de propietarios" />
            <div className="flex items-center gap-2 sm:gap-6">
              <div className="hidden md:block text-right">
                <p className="font-semibold text-amber-900 leading-tight">{user?.name}</p>
                <VerifiedLabel status={user?.verification_status} />
              </div>

              <NotificationsBell token={token} onNavigate={setActiveTab} />

              <button
                onClick={() => setShowProfile(true)}
                className="flex items-center gap-2 rounded-full p-1 md:pr-4 border border-amber-200 text-amber-900 hover:bg-amber-50 transition-colors"
                aria-label="Mi perfil"
              >
                <span className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-700 text-white text-sm font-bold flex items-center justify-center">{initials(user?.name)}</span>
                <span className="hidden md:inline font-semibold text-sm">Mi perfil</span>
              </button>
              <button
                onClick={logout}
                className="text-amber-800/80 hover:text-red-600 font-medium transition-colors flex items-center space-x-1 p-2 sm:p-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Salir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <FounderBanner
        ctaLabel="Evaluar gratis"
        onCta={() => setActiveTab('rate')}
        secondaryCtaLabel="Consulta gratis"
        onSecondaryCta={() => setActiveTab('search')}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 text-left">
        {/* Welcome Section */}
        <section className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-amber-500 via-amber-600 to-amber-800 text-white p-6 sm:p-10 mb-6 sm:mb-8 shadow-xl shadow-amber-300/40">
          <BrandBackdrop />
          <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
            <div className="min-w-0">
              <p className="text-amber-100 text-xs font-semibold uppercase tracking-[0.18em]">El bur&oacute; de cr&eacute;dito para inquilinos morosos</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Hola, {user?.name?.split(' ')[0]} <span aria-hidden="true">&#128075;</span></h2>
              <p className="mt-2 text-amber-50/95 text-base sm:text-lg max-w-xl">Consulta a tu pr&oacute;ximo inquilino antes de firmar, o eval&uacute;a a los que ya tuviste para proteger a otros propietarios.</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button onClick={() => setActiveTab('search')} className="bg-white text-amber-800 font-bold px-6 py-3 rounded-full hover:bg-amber-50 shadow-lg shadow-amber-950/20 inline-flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.2-5.2M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" /></svg>
                  Buscar inquilino
                </button>
                <button onClick={() => setActiveTab('rate')} className="border-2 border-white/60 text-white font-semibold px-6 py-2.5 rounded-full hover:bg-white/10 inline-flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M11.5 3.5l2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.6-4.8 2.6.9-5.4L3.7 9.2l5.4-.8 2.4-4.9z" /></svg>
                  Evaluar inquilino
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur p-4">
                <p className="text-3xl font-extrabold">{stats.ratings_given || 0}</p>
                <p className="text-sm text-amber-50/90">Evaluaciones hechas</p>
              </div>
              <div className="rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur p-4 min-w-0">
                <p className="text-xl sm:text-2xl font-extrabold truncate">{calculateTimeOnPlatform(user?.created_at || stats.first_rating_date)}</p>
                <p className="text-sm text-amber-50/90">En la plataforma</p>
              </div>
              <div className="col-span-2 rounded-2xl bg-amber-950/25 ring-1 ring-white/15 p-4 flex items-center gap-3">
                <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${user?.verification_status === 'verified' ? 'bg-emerald-400 text-emerald-950' : 'bg-white/20 text-white'}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.6-4A12 12 0 0112 3a12 12 0 01-8.6 3A12 12 0 003 9c0 5.6 3.8 10.3 9 11.6 5.2-1.3 9-6 9-11.6 0-1-.1-2-.4-3z" /></svg>
                </span>
                <div className="min-w-0">
                  <p className="font-bold leading-tight">{user?.verification_status === 'verified' ? 'Propietario verificado' : user?.verification_status === 'pending' ? 'Verificaci\u00f3n en revisi\u00f3n' : 'Verifica tu identidad'}</p>
                  <p className="text-xs text-amber-50/85">{user?.verification_status === 'verified' ? 'Tus evaluaciones llevan la insignia de confianza.' : 'Necesaria para evaluar inquilinos.'}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-[2rem] shadow-sm border border-amber-100">
          <div className="p-2 sm:p-3 border-b border-amber-100">
            <nav className="flex gap-1 sm:gap-2 overflow-x-auto no-scrollbar" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('search')}
                className={`flex-shrink-0 py-2.5 px-4 sm:px-5 rounded-full font-semibold text-sm transition-colors ${
                  activeTab === 'search'
                    ? 'bg-amber-950 text-amber-50 shadow-md'
                    : 'text-amber-900/70 hover:bg-amber-50 hover:text-amber-950'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="whitespace-nowrap">Buscar Inquilinos</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('rate')}
                className={`flex-shrink-0 py-2.5 px-4 sm:px-5 rounded-full font-semibold text-sm transition-colors ${
                  activeTab === 'rate'
                    ? 'bg-amber-950 text-amber-50 shadow-md'
                    : 'text-amber-900/70 hover:bg-amber-50 hover:text-amber-950'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                  <span className="whitespace-nowrap">Evaluar Inquilino</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('analytics')}
                className={`flex-shrink-0 py-2.5 px-4 sm:px-5 rounded-full font-semibold text-sm transition-colors ${
                  activeTab === 'analytics'
                    ? 'bg-amber-950 text-amber-50 shadow-md'
                    : 'text-amber-900/70 hover:bg-amber-50 hover:text-amber-950'
                }`}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="whitespace-nowrap">Panel de An&aacute;lisis</span>
                </div>
              </button>

              {user?.is_admin && (
                <button
                  onClick={() => setActiveTab('admin')}
                  className={`flex-shrink-0 py-2.5 px-4 sm:px-5 rounded-full font-semibold text-sm transition-colors ${
                    activeTab === 'admin'
                      ? 'bg-amber-950 text-amber-50 shadow-md'
                      : 'text-amber-900/70 hover:bg-amber-50 hover:text-amber-950'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="whitespace-nowrap">Verificaciones</span>
                  </div>
                </button>
              )}

            </nav>
          </div>

          <div className="p-4 sm:p-8">
            {activeTab === 'search' && <TenantSearch token={token} />}
            {activeTab === 'rate' && (user?.verification_status === 'verified' ? <RateTenant /> : <VerificationGate />)}
            {activeTab === 'analytics' && <AnalyticsDashboard token={token} />}
            {activeTab === 'admin' && user?.is_admin && <AdminVerifications />}

          </div>
        </div>
      </main>

      {/* Profile Modal */}
      {showProfile && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[400]"
          onClick={() => setShowProfile(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full shadow-xl border border-amber-200 flex flex-col overflow-hidden"
            style={{ maxHeight: 'calc(100dvh - 2rem)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header - always visible; only the body scrolls */}
            <div className="bg-amber-50/60 border-b border-amber-200 px-4 sm:px-8 py-4 sm:py-5 flex-shrink-0 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-amber-200 flex-shrink-0">
                    <svg className="w-6 h-6 sm:w-7 sm:h-7 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-800 truncate">Mi perfil</h3>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">Informaci&oacute;n de cuenta y estad&iacute;sticas</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowProfile(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-8 overflow-y-auto flex-1 min-h-0">
              {/* Profile Information */}
              <div className="space-y-4">
                <div className="bg-amber-50/60 rounded-xl p-4 sm:p-6 border border-amber-200">
                  <h4 className="text-base sm:text-lg font-bold text-amber-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Informaci&oacute;n Personal
                  </h4>
                  <div className="space-y-3">
                    <div className="flex flex-wrap justify-between items-center gap-1 py-2 border-b border-amber-200/50">
                      <span className="text-amber-700/80 font-medium">Nombre:</span>
                      <span className="text-amber-900 font-bold text-right break-words">{user?.name}</span>
                    </div>
                    <div className="flex flex-wrap justify-between items-center gap-1 py-2 border-b border-amber-200/50">
                      <span className="text-amber-700/80 font-medium">Email:</span>
                      <span className="text-amber-900 text-right break-all min-w-0">{user?.email}</span>
                    </div>
                    {user?.company_name && (
                      <div className="flex flex-wrap justify-between items-center gap-1 py-2 border-b border-amber-200/50">
                        <span className="text-amber-700/80 font-medium">Empresa:</span>
                        <span className="text-amber-900 text-right break-words">{user.company_name}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Statistics */}
                <div className="bg-amber-50/60 rounded-xl p-4 sm:p-6 border border-amber-200">
                  <h4 className="text-base sm:text-lg font-bold text-amber-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Estad&iacute;sticas Profesionales
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="text-center bg-white/90 rounded-xl p-4 sm:p-6 border border-amber-300 shadow-sm">
                      <div className="text-2xl sm:text-3xl font-bold text-amber-600 mb-2">{stats.ratings_given || 0}</div>
                      <div className="text-sm text-amber-700/80 font-medium">Evaluaciones Realizadas</div>
                    </div>
                    <div className="text-center bg-white/90 rounded-xl p-4 sm:p-6 border border-green-200 shadow-sm">
                      <div className="text-2xl sm:text-3xl font-bold text-green-600 mb-2">
                        {calculateTimeOnPlatform(user?.created_at || stats.first_rating_date)}
                      </div>
                      <div className="text-sm text-green-700 font-semibold">Tiempo en la Plataforma</div>
                    </div>
                  </div>
                  
                  {/* PDF Export Button */}
                  <div className="mt-4 pt-4 border-t border-amber-200">
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/reports/landlord-stats-pdf`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            }
                          });
                          
                          if (response.ok) {
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `estadisticas_propietario_${user?.name?.replace(/\s+/g, '_')}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } else {
                            alert('Error al generar el reporte PDF');
                          }
                        } catch (error) {
                          console.error('Error:', error);
                          alert('Error al generar el reporte PDF');
                        }
                      }}
                      className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-4 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2 shadow-sm"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Descargar Reporte PDF</span>
                    </button>
                  </div>
                </div>

                {/* Account Status */}
                <div className="bg-emerald-50 rounded-xl p-4 sm:p-6 border border-emerald-200">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h4 className="text-base sm:text-lg font-bold text-amber-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        Estado de la Cuenta
                      </h4>
                      <p className="text-amber-800/80">{user?.verification_status === 'verified' ? 'Tu cuenta est\u00e1 activa y tu identidad verificada' : 'Tu cuenta est\u00e1 activa. Verifica tu identidad para evaluar inquilinos.'}</p>
                    </div>
                    <div className="flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-3 rounded-xl shadow-sm">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-bold">Activa</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { Dashboard };
